import { AbstractDocument, Identifiable, Timestampable, Untrackable } from '../../../abstract/Document';
import { IAddonPlaceTopSpot } from './placeTopSpot.types';
import { arrayRemove, arrayUnion, serverTimestamp, Timestamp } from 'firebase/firestore';

export type PlaceTopSpotStatus = { isAssigned: boolean; canAssign: boolean };

@Identifiable
@Timestampable
@Untrackable
export class AddonPlaceTopSpot extends AbstractDocument<IAddonPlaceTopSpot> {
  readonly collections = {};

  async getSupplierIds() {
    const data = await this.get(true);
    return (data?.defaultBoost?.suppliers || []).map((supplierId) => ({ supplierId }));
  }

  async canAssign(params: { supplierId: string }, snapshot?: IAddonPlaceTopSpot): Promise<PlaceTopSpotStatus> {
    if (!params.supplierId) {
      return { isAssigned: false, canAssign: false };
    }
    const data = snapshot || (await this.get(true));
    const canAssign = !data?.defaultBoost || data.defaultBoost.suppliers?.length < data.defaultBoost?.limit;
    const isAssigned = data?.defaultBoost?.suppliers.includes(params.supplierId) || false;
    return { canAssign, isAssigned };
  }

  async assign(params: { supplierId: string }) {
    const data = await this.get(true);
    if (data) {
      return this.assignExisting(params, data);
    } else {
      return this.assignNew(params);
    }
  }

  async unassign(params: { supplierId: string }) {
    const data = await this.get(true);
    if (data?.defaultBoost?.suppliers?.includes(params.supplierId)) {
      await this.set(
        {
          defaultBoost: {
            ...data.defaultBoost,
            suppliers: arrayRemove(params.supplierId),
          },
          updatedAt: serverTimestamp(),
        },
        true,
      );
    }
    return true;
  }

  private async assignNew(params: { supplierId: string }) {
    const placeTopSpot: IAddonPlaceTopSpot = {
      id: 'placeTopSpot',
      createdAt: serverTimestamp() as Timestamp,
      defaultBoost: {
        suppliers: [params.supplierId],
        limit: 1,
      },
    };
    await this.create(placeTopSpot);
    return true;
  }

  private async assignExisting(params: { supplierId: string }, data: IAddonPlaceTopSpot) {
    const { isAssigned, canAssign } = await this.canAssign({ supplierId: params.supplierId }, data);
    if (isAssigned || !canAssign) {
      return false;
    }
    const patch = {
      defaultBoost: {
        limit: data.defaultBoost?.limit || 1,
        suppliers: arrayUnion(params.supplierId),
      },
      updatedAt: serverTimestamp(),
    };
    await this.set(patch, true);
    return true;
  }
}
